import pluralize from 'pluralize';

import {Button, Carousel, Text} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {ActionItemModal} from '#component/modal';
import {HealthFactorCompactCard} from '#component/partial';
import {useCarousel, useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {QuestionCarouselProps} from './QuestionCarousel.types';

/**
 * Renders a carousel displaying a list of cards representing CheckInQuestions,
 * for use on the CheckIn summary page.
 */
export const QuestionCarousel = ({headline, questions = []}: QuestionCarouselProps) => {
  const {isMobile} = useViewportSize();
  const {ref, goToNextSlide, goToPreviousSlide} = useCarousel({
    gap: 16,
    slideWidth: isMobile ? 300 : 328,
  });

  return (
    <div className={sprinkles({flex: 'flex', dir: 'col', gap: 16})}>
      <div className={sprinkles({flex: 'flex', content: 'between', items: 'center', pX: [16, 64]})}>
        <Text color="deepBurgundy" variant="heading3">
          {headline}
        </Text>

        <div className={sprinkles({flex: ['none', 'flex'], gap: 12})}>
          <Button
            aria-label="Go to previous slide"
            onClick={goToPreviousSlide}
            variant="secondary"
            icon="ArrowLeft"
          />

          <Button
            aria-label="Go to next slide"
            onClick={goToNextSlide}
            variant="secondary"
            icon="ArrowRight"
          />
        </div>
      </div>

      <Carousel ref={ref} paddings={[16, 64, 64]} widths={['100%', '100%', '100%']}>
        {questions.map(
          ({
            actionItemTitle,
            answerBoolean,
            benefits,
            healthFactor,
            hitCount,
            id,
            imageUrl,
            impact,
            tips,
          }) => (
            <ModalRoot key={id}>
              <HealthFactorCompactCard
                as={ModalTrigger}
                ctaLabel={answerBoolean ? 'View benefits' : 'View tips'}
                eyebrow={`Youʼve hit this ${pluralize('time', hitCount, true)}`}
                headline={actionItemTitle}
                imageUrl={imageUrl}
                impact={impact}
                practicing={answerBoolean}
              />

              <ActionItemModal
                headline={healthFactor?.title}
                content={answerBoolean ? benefits : tips}
              />
            </ModalRoot>
          ),
        )}
      </Carousel>
    </div>
  );
};
